import { useNotifications } from '@risksmart-app/components/src/notifications/useNotifications';
import type { AttestationStatusResponseRow } from '@risksmart-app/trpc/types/attestation-record.types';
import type { GetAttestationStatusQuery } from '@risksmart-app/web-graphql-client/generated/graphql';
import { useQuery } from '@tanstack/react-query';
import { useEffect } from 'react';
import { mapTRPCRefetch } from 'src/utils';

import { useIsFeatureVisibleToOrg } from '@/utils/featureFlags';
import { useTRPC } from '@/utils/trpc';

/**
 * Maps TRPC attestation status data to match the GraphQL query structure
 */
function mapTrpcAttestationStatusToGraphQL(
  trpcData: AttestationStatusResponseRow[]
): GetAttestationStatusQuery {
  return {
    attestation_record: trpcData,
  };
}

export const useGetAttestationStatus = (
  parentId: string,
  userId: string,
  skip?: boolean
) => {
  const trpcEnabled = useIsFeatureVisibleToOrg('trpc');
  const trpc = useTRPC();
  const { addNotification } = useNotifications();

  const {
    data: trpcData,
    isLoading: trpcLoading,
    refetch: trpcRefetch,
    error: trpcError,
  } = useQuery({
    ...trpc.frontend.attestation.status.queryOptions({ parentId, userId }),
    enabled: trpcEnabled && !skip,
  });

  // Handle TRPC errors
  useEffect(() => {
    if (trpcEnabled && trpcError) {
      addNotification({
        type: 'error',
        content: <>{trpcError.message}</>,
      });
    }
  }, [trpcEnabled, trpcError, addNotification]);

  // Transform TRPC data to match the same structure as GraphQL query
  const data = trpcData
    ? mapTrpcAttestationStatusToGraphQL(trpcData)
    : undefined;

  return {
    loading: trpcLoading,
    data,
    refetch: () =>
      mapTRPCRefetch(trpcRefetch, mapTrpcAttestationStatusToGraphQL),
    error: trpcError,
  };
};
